import React from 'react';
import {
  clampQty,
  scanCountLineState,
  scanCountPastTense,
  type ScanCountCounts,
  type ScanCountEntry,
  type ScanCountLine,
  type ScanCountVerb,
} from '@/domain/warehouse';
import SerialChips from './SerialChips';

export type ScanCountMode = 'scan' | 'count';

type ScanCountLinesProps = {
  lines: readonly ScanCountLine[];
  counts: ScanCountCounts;
  mode: ScanCountMode;
  verb: ScanCountVerb;
  /** The line the last scan matched; it stays outlined until the next scan. */
  recentLineId: string | null;
  disabled?: boolean;
  onQtyChange: (lineId: string, qty: number) => void;
  onToggleSerial: (lineId: string, serialId: string) => void;
};

/**
 * The lines to receive (or pick), in the record's order so rows never move while scanning.
 * Scan mode shows progress only; Count mode adds quantity steppers and serial toggles.
 * Rows are memoised, so a scan re-renders only the line it changed.
 */
export default function ScanCountLines({
  lines,
  counts,
  mode,
  verb,
  recentLineId,
  disabled = false,
  onQtyChange,
  onToggleSerial,
}: ScanCountLinesProps) {
  return (
    <ol className="scan-count-lines" aria-label={`Lines to ${verb.toLowerCase()}`}>
      {lines.map((line) => (
        <ScanCountLineRow
          key={line.id}
          line={line}
          entry={counts[line.id]}
          mode={mode}
          verb={verb}
          recent={recentLineId === line.id}
          disabled={disabled}
          onQtyChange={onQtyChange}
          onToggleSerial={onToggleSerial}
        />
      ))}
    </ol>
  );
}

type RowProps = {
  line: ScanCountLine;
  entry: ScanCountEntry | undefined;
  mode: ScanCountMode;
  verb: ScanCountVerb;
  recent: boolean;
  disabled: boolean;
  onQtyChange: (lineId: string, qty: number) => void;
  onToggleSerial: (lineId: string, serialId: string) => void;
};

const ScanCountLineRow = React.memo(function ScanCountLineRow({
  line,
  entry,
  mode,
  verb,
  recent,
  disabled,
  onQtyChange,
  onToggleSerial,
}: RowProps) {
  const nameId = React.useId();
  const state = scanCountLineState(line, entry);
  const past = scanCountPastTense(verb);
  const serials = line.serials ?? [];
  const isSerial = line.tracking === 'serial';
  const tracking = isSerial ? (serials.length === 1 ? serials[0].code : 'each unit') : 'by quantity';
  const meta = [line.sku, tracking].filter(Boolean).join(' · ');
  const hasUnlisted = Object.keys(state.unlisted).length > 0;
  const showChips =
    isSerial &&
    (mode === 'count'
      ? serials.some((serial) => !serial.done) || hasUnlisted
      : (!state.complete && serials.length > 1) || hasUnlisted);

  function toggle(serialId: string) {
    onToggleSerial(line.id, serialId);
  }

  return (
    <li
      className="scan-count-line"
      data-state={state.complete ? 'complete' : 'open'}
      data-recent={recent ? 'true' : undefined}
      data-line-id={line.id}
    >
      <div className="scan-count-line__main">
        <strong id={nameId} className="scan-count-line__name">
          {line.name}
        </strong>
        {meta ? <small className="scan-count-line__meta">{meta}</small> : null}
      </div>
      <div className="scan-count-line__count">
        {mode === 'count' && !isSerial ? (
          <QtyStepper
            line={line}
            counted={state.counted}
            room={state.room}
            nameId={nameId}
            past={past}
            disabled={disabled}
            onChange={onQtyChange}
          />
        ) : null}
        <span className="scan-count-line__qty" aria-hidden="true">
          {state.done} / {line.expected}
        </span>
        <span className="sr-only">
          {state.done} of {line.expected} {past}
        </span>
      </div>
      {showChips ? (
        <SerialChips
          lineName={line.name}
          serials={serials}
          countedIds={state.serialIds}
          unlisted={state.unlisted}
          interactive={mode === 'count'}
          full={state.counted >= state.room}
          disabled={disabled}
          donePast={past}
          onToggle={toggle}
        />
      ) : null}
    </li>
  );
});

type QtyStepperProps = {
  line: ScanCountLine;
  counted: number;
  room: number;
  nameId: string;
  past: string;
  disabled: boolean;
  onChange: (lineId: string, qty: number) => void;
};

/** One fewer / number field / one more. The field accepts 0 up to the units still due. */
function QtyStepper({ line, counted, room, nameId, past, disabled, onChange }: QtyStepperProps) {
  // What is typed stays on screen until the field is left, so clearing it to type a new number
  // does not snap back to 0 mid-edit.
  const [text, setText] = React.useState<string | null>(null);
  // At a bound the buttons are aria-disabled rather than disabled, so a keyboard user pressing
  // "One more" up to the last unit keeps focus on the button.
  const atMin = disabled || counted <= 0;
  const atMax = disabled || counted >= room;

  function decrease() {
    if (atMin) return;
    setText(null);
    onChange(line.id, counted - 1);
  }

  function increase() {
    if (atMax) return;
    setText(null);
    onChange(line.id, counted + 1);
  }

  function handleChange(event: React.ChangeEvent<HTMLInputElement>) {
    const raw = event.target.value;
    const value = Number(raw);
    if (raw.trim() === '' || !Number.isFinite(value)) {
      setText(raw);
      return;
    }
    const clamped = clampQty(line, value);
    setText(clamped === value ? raw : String(clamped));
    onChange(line.id, clamped);
  }

  function handleBlur() {
    setText(null);
  }

  return (
    <div className="scan-count-stepper">
      <button
        type="button"
        className="scan-count-stepper__button"
        aria-label="One fewer"
        aria-describedby={nameId}
        aria-disabled={atMin || undefined}
        onClick={decrease}
      >
        {'−'}
      </button>
      <input
        type="number"
        className="scan-count-stepper__input"
        inputMode="numeric"
        min={0}
        max={room}
        step={1}
        value={text ?? String(counted)}
        aria-label={`${line.name}: units ${past} in this count`}
        disabled={disabled || room === 0}
        onChange={handleChange}
        onBlur={handleBlur}
      />
      <button
        type="button"
        className="scan-count-stepper__button"
        aria-label="One more"
        aria-describedby={nameId}
        aria-disabled={atMax || undefined}
        onClick={increase}
      >
        +
      </button>
    </div>
  );
}
